import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Star, CheckCircle, Calendar, Gauge } from 'lucide-react';

/**
 * FeaturedVehicleCard - Enhanced vehicle card for home page
 * Shows vehicle details along with dealer information
 */
const FeaturedVehicleCard = ({ vehicle, distance = null }) => {
  const navigate = useNavigate();

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  // Format mileage
  const formatMileage = (mileage) => {
    return new Intl.NumberFormat('en-US').format(mileage);
  };

  const handleBookTestDrive = () => {
    navigate(`/vehicle/${vehicle._id}`);
  };

  return (
    <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl border border-app-border-light dark:border-app-border overflow-hidden hover:border-app-accent-light dark:hover:border-app-accent transition-all duration-200 hover:shadow-xl group">
      {/* Vehicle Image */}
      <div className="relative h-48 bg-app-bg-tertiary-light dark:bg-app-bg-tertiary overflow-hidden">
        <img
          src={vehicle.image || 'https://via.placeholder.com/400x300?text=Vehicle+Image'}
          alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
          decoding="async"
        />

        {/* Featured badge */}
        {vehicle.featured && (
          <div className="absolute top-3 left-3 bg-app-accent text-white px-3 py-1 rounded-lg text-xs font-semibold shadow-lg">
            Featured
          </div>
        )}

        {/* Low mileage badge */}
        {vehicle.mileage < 40000 && (
          <div className="absolute top-3 right-3 bg-app-success text-white px-3 py-1 rounded-lg text-xs font-semibold shadow-lg">
            Low Mileage
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Vehicle Title & Price */}
        <div className="mb-3">
          <h3 className="text-lg font-bold text-app-text-light dark:text-app-text mb-1">
            {vehicle.year} {vehicle.make} {vehicle.model}
          </h3>
          <p className="text-2xl font-bold text-app-accent">
            {formatPrice(vehicle.price)}
          </p>
        </div>

        {/* Vehicle Stats */}
        <div className="flex items-center gap-4 mb-4 text-sm text-app-text-secondary-light dark:text-app-text-secondary">
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            <span>{vehicle.year}</span>
          </div>
          <div className="flex items-center gap-1">
            <Gauge className="w-4 h-4" />
            <span>{formatMileage(vehicle.mileage)} mi</span>
          </div>
          {vehicle.type && (
            <div className="px-2 py-0.5 bg-app-bg-tertiary-light dark:bg-app-bg-tertiary rounded text-xs font-medium">
              {vehicle.type}
            </div>
          )}
        </div>

        {/* Dealer Information */}
        {vehicle.dealer && (
          <div className="mb-4 pb-4 border-b border-app-border-light dark:border-app-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-app-accent" />
                <span className="text-sm text-app-text-light dark:text-app-text font-medium">
                  {vehicle.dealer.name}
                </span>
                {vehicle.dealer.verified && (
                  <CheckCircle className="w-4 h-4 text-app-success" />
                )}
              </div>

              {/* Dealer Rating */}
              {vehicle.dealer.rating && vehicle.dealer.rating.count > 0 && (
                <div className="flex items-center gap-1">
                  <Star className="w-3 h-3 fill-app-warning text-app-warning" />
                  <span className="text-xs font-semibold text-app-text-light dark:text-app-text">
                    {vehicle.dealer.rating.average.toFixed(1)}
                  </span>
                </div>
              )}
            </div>

            {/* Distance */}
            {distance && (
              <p className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mt-1 ml-6">
                {distance} miles away
              </p>
            )}
          </div>
        )}

        {/* Availability Window */}
        <div className="mb-4">
          <span className="inline-flex items-center px-3 py-1 rounded-lg bg-app-success/10 text-app-success text-sm font-medium">
            Available for Test Drive
          </span>
        </div>

        {/* CTA Button */}
        <button
          onClick={handleBookTestDrive}
          className="w-full bg-app-accent hover:bg-app-accent/90 text-white py-3 rounded-xl font-semibold transition-all duration-200 hover:shadow-lg"
        >
          Reserve Test Drive
        </button>
      </div>
    </div>
  );
};

export default FeaturedVehicleCard;
